import React, { useRef } from "react";
import "./MessageInput.css";

const MessageInput = React.forwardRef(
  (
    {
      value,
      onChange,
      onSend,
      onKeyPress,
      onFileUpload,
      attachments,
      onRemoveAttachment,
      placeholder,
      maxLength,
      allowFileUpload,
      disabled,
    },
    ref
  ) => {
    const fileInputRef = useRef(null);

    const handleFileSelect = (e) => {
      const files = Array.from(e.target.files);
      if (files.length > 0) {
        onFileUpload(files);
      }
      // Reset input value to allow selecting the same file again
      e.target.value = "";
    };

    const handleFileButtonClick = () => {
      if (allowFileUpload && fileInputRef.current) {
        fileInputRef.current.click();
      }
    };

    const formatFileSize = (bytes) => {
      if (bytes === 0) return "0 Bytes";
      const k = 1024;
      const sizes = ["Bytes", "KB", "MB", "GB"];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
    };

    return (
      <div className="chatbot-input-container">
        {attachments.length > 0 && (
          <div className="chatbot-attachments-preview">
            {attachments.map((attachment) => (
              <div key={attachment.id} className="chatbot-attachment-preview">
                <div className="chatbot-attachment-preview-icon">
                  {attachment.type.startsWith("image/") ? (
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <rect
                        x="3"
                        y="3"
                        width="18"
                        height="18"
                        rx="2"
                        ry="2"
                        stroke="currentColor"
                        strokeWidth="2"
                      />
                      <circle
                        cx="8.5"
                        cy="8.5"
                        r="1.5"
                        stroke="currentColor"
                        strokeWidth="2"
                      />
                      <polyline
                        points="21,15 16,10 5,21"
                        stroke="currentColor"
                        strokeWidth="2"
                      />
                    </svg>
                  ) : (
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <polyline
                        points="14,2 14,8 20,8"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </div>
                <div className="chatbot-attachment-preview-info">
                  <div className="chatbot-attachment-preview-name">
                    {attachment.name}
                  </div>
                  <div className="chatbot-attachment-preview-size">
                    {formatFileSize(attachment.size)}
                  </div>
                </div>
                <button
                  className="chatbot-attachment-preview-remove"
                  onClick={() => onRemoveAttachment(attachment.id)}
                  type="button"
                  aria-label="Remove attachment"
                >
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M18 6L6 18M6 6L18 18"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="chatbot-input-wrapper">
          <div className="chatbot-input-field">
            <textarea
              ref={ref}
              value={value}
              onChange={(e) => onChange(e.target.value)}
              onKeyPress={onKeyPress}
              placeholder={placeholder}
              maxLength={maxLength}
              disabled={disabled}
              rows="1"
              className="chatbot-textarea"
            />
            <div className="chatbot-input-footer">
              <div className="chatbot-char-count">
                {value.length}/{maxLength}
              </div>
            </div>
          </div>

          <button
            className="chatbot-send-button"
            onClick={onSend}
            disabled={disabled || (!value.trim() && attachments.length === 0)}
            type="button"
            aria-label="Send message"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M22 2L11 13M22 2L15 22L11 13M22 2L2 9L11 13"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          multiple
          onChange={handleFileSelect}
          style={{ display: "none" }}
          accept="image/*,.pdf,.doc,.docx,.txt"
        />
      </div>
    );
  }
);

MessageInput.displayName = "MessageInput";

export default MessageInput;
